import React, { useEffect, useState } from "react";
import cn from "classnames";
import { ConfigProvider, Pagination } from "antd";
import useDarkMode from "use-dark-mode";

import styles from "./TotesTable.module.sass";
import Checkbox from "../../../components/Checkbox";
import Card from "../../../components/Card";
import ToteTableRow from "./Row";
import CustomToast from "../../../components/CustomToast";
import { FETCH_TOTES } from "../../../API/Totes";

const TotesTable = ({
  handleCheckboxChange,
  checkedItems,
  page,
  setPage,
  totalItems,
  setTotalItems,
  itemsPerPage,
  setItemsPerPage,
  customFieldTrigger,
  activeTab,
  refetchTrigger,
  setToteMarkedToDelete,
  setToteMarkedToEmpty,
  search
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const [data, setData] = useState([]);

  const isCurrentPageAllSelected = () => {
    if (data.length === 0) return false;
    return data.every(item => checkedItems[item.tote_id]);
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    const newCheckedItems = { ...checkedItems };
    data.forEach((item) => {
      newCheckedItems[item.tote_id] = checked;
    });    
    handleCheckboxChange(e, newCheckedItems);
  };

  const handlePageChange = (currentPage, pageSize) => {
    setPage(currentPage);
    if (pageSize !== itemsPerPage) {
      setItemsPerPage(pageSize);
    }
  };

  const fetchData = async () => {
    try {
      const queryParams = [
        { key: "page", value: page },
        { key: "pageSize", value: itemsPerPage },
      ];
      if (activeTab !== "all") {
        queryParams.push({ 
          key: "status", 
          value: activeTab.toUpperCase() 
        });
      }
      if (search) {
        queryParams.push({
          key: "keywords",
          value: encodeURIComponent(search),
        });
      }
      const response = await FETCH_TOTES(queryParams);
      setData(response.result);
      setTotalItems(response.count);
    } catch (error) {
      CustomToast.fire({
        icon: "error",
        title: "Failed to fetch totes",
      });
    }
  };

  const darkMode = useDarkMode(false);
  const themeConfig = {
    token: {
      colorPrimary: "#1890ff",
      colorTextBase: darkMode.value ? "#ffffff" : "#000000",
      colorBgContainer: darkMode.value ? "#1f1f1f" : "#ffffff",
      colorBgElevated: darkMode.value ? "#2f2f2f" : "#ffffff",
      colorText: darkMode.value ? "#ffffff" : "#000000",
      buttonBoxShadow: darkMode.value ? "none" : "0 2px 0 rgb(230, 247, 255)",
      paginationBg: darkMode.value ? "#2f2f2f" : "#ffffff",
      paginationItemBg: darkMode.value ? "#2f2f2f" : "#ffffff",
      paginationItemBgHover: darkMode.value ? "#3a3a3a" : "#f5f5f5",
      paginationItemActiveBg: darkMode.value ? "#3a3a3a" : "#e6f7ff",
      paginationItemLinkBg: darkMode.value ? "#2f2f2f" : "#ffffff",
    },
  };

  useEffect(() => {
    fetchData();
  }, [customFieldTrigger, page, itemsPerPage, activeTab, refetchTrigger, search]);

  return (
    <ConfigProvider theme={themeConfig}>
      <div className={styles.market}>
        <Card className={styles.Card}>
          <div >
            <div className={styles.table}>
              <div className={styles.row}>
                <div className={styles.col}>
                  <Checkbox
                    className={styles.checkbox}
                    checked={isCurrentPageAllSelected()}
                    onChange={handleSelectAll}
                  />
                  
                </div>
                <div className={cn(styles.col)}>Tote ID</div>
                <div className={cn(styles.col)}>Contents</div>
                <div className={cn("whitespace-nowrap",styles.col)}>Used By</div>
                <div className={cn(styles.col)}>Status</div>
              </div>
              {data.map((item, index) => (
                <ToteTableRow
                  key={index}
                  data={item}
                  checkedItems={checkedItems}
                  handleCheckboxChange={handleCheckboxChange}
                  setToteMarkedToDelete={setToteMarkedToDelete}
                  setToteMarkedToEmpty={setToteMarkedToEmpty}
                  
                />
              ))}
            </div>
          </div>
          <div className={styles.foot}>
            <Pagination
              current={page}
              total={totalItems}
              pageSize={itemsPerPage}
              onChange={handlePageChange}
              onShowSizeChange={(current, size) => {
                setItemsPerPage(size);
                setPage(1);
                setSelectAll(false);
              }}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} totes`
              }
              showSizeChanger
            />
          </div>
        </Card>
      </div>
    </ConfigProvider>
  );
};

export default TotesTable;
